import React, { useMemo, useState, useEffect } from 'react';

import { Control, SettingsItemWrapper } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import type { TOriginSize } from '@vlabs/monokit/shared/ui/canvas';

import { REGEX } from './constants';
import { normalizeAbsValue, normalizePercentValue } from './normalizeValue';
import st from './Rectangle.module.sass';
import { convertRegionFromAbsToPercent, roundCoordinates } from '../../../regionConverters';
import type { TRectangle, TRegionRectangle } from '../../types';

interface RectangleProps {
  region?: TRegionRectangle;
  mediaSize?: TOriginSize;
  onChange?: (region: TRegionRectangle) => void;
}

export function Rectangle({
  region = undefined,
  mediaSize = undefined,
  onChange = undefined,
}: RectangleProps): React.JSX.Element {
  const { t } = useTranslation();

  const [temporaryInputValue, setTemporaryInputValue] = useState<TRectangle | object>({});
  const [regionModeCoordinates, setRegionModeCoordinates] = useState<TRectangle | undefined>(undefined);

  useEffect(() => {
    setRegionModeCoordinates(region && region.mode === 'abs'
      ? region
      : convertRegionFromAbsToPercent(region, mediaSize));
  }, [region]);

  useEffect(() => {
    if (region && region.mode === 'abs') {
      onChange?.({
        ...region,
        ...roundCoordinates(region),
      });
    }
    setTemporaryInputValue({});
  }, [region?.mode]);

  const {
    x, y, width, height,
  } = regionModeCoordinates ?? {};

  const bbox = useMemo(() => {
    return {
      x: 0,
      y: 0,
      width: region?.mode === 'abs' ? mediaSize?.width : 100,
      height: region?.mode === 'abs' ? mediaSize?.height : 100,
    };
  }, [mediaSize, region?.mode]);

  const commonStylesProps = {
    className: st.SettingsItem,
    titleClassName: st.SettingsItem__Title,
    contentClassName: st.SettingsItem__Content,
  };

  const onChangeCoords = (inputValue: string, field: keyof TRectangle) => {
    if (!regionModeCoordinates) return;

    let value = inputValue;

    if (region?.mode === 'percent') {
      // Убираем из поле ввода все символы, кроме цифр и первой точки
      value = value.replace(REGEX.excludeNumbersAndDot, '').replace(REGEX.firstAndLastDots, '$1');

      /* Если в поле ввода число с висячей точкой в конце формата "XX.", то:
      - оставляем этот ввод в поле
      - не отправляем это значение для обновления данных региона
      - ждём или удаления точки, или появления цифры после неё
      */
      if (REGEX.numbersWithEndingDot.test(value)) {
        setTemporaryInputValue((prev) => ({ ...prev, [field]: value }));
        return;
      }
      setTemporaryInputValue({});
    }

    const normalizedValue = region?.mode === 'abs'
      ? normalizeAbsValue(value, field, regionModeCoordinates, bbox)
      : normalizePercentValue(value, field, regionModeCoordinates, bbox, mediaSize);

    onChange?.({
      ...region as TRegionRectangle,
      [field]: normalizedValue,
    });
  };

  return (
    <div className={st.Root}>
      <SettingsItemWrapper
        title="X"
        {...commonStylesProps}
      >
        <Control.Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCoords(e.target.value, 'x')}
          value={temporaryInputValue.x ?? x}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title="Y"
        {...commonStylesProps}
      >
        <Control.Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCoords(e.target.value, 'y')}
          value={temporaryInputValue.y ?? y}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('roi.modal.panel.width')}
        {...commonStylesProps}
      >
        <Control.Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCoords(e.target.value, 'width')}
          value={temporaryInputValue.width ?? width}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('roi.modal.panel.height')}
        {...commonStylesProps}
      >
        <Control.Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeCoords(e.target.value, 'height')}
          value={temporaryInputValue.height ?? height}
        />
      </SettingsItemWrapper>
    </div>
  );
}
